import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import WarningIcon from '@mui/icons-material/Warning';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DataDetailToolbar, LoadingSpinner, CSVDropzone, StepTracker } from '../../components';
import { useFeathers } from '../../app/util';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { CreateMeasureList, MeasureList, MeasureLabeledColumn, MeasureColumnLabel, MeasureLabeledDataItem } from '../../app/feathers/measure-lists';
import { Election } from '../../app/feathers/elections/Election';
import { User } from '../../app/feathers/users/User';
import styled from 'styled-components';
import DataOperationsTable from '../UploadCandidateList/DataOperationsTable';
import DistrictCell from '../UploadCandidateList/DistrictCell';
import TitleCell from './TitleCell';
import { MeasureScoutingStepContent } from './MeasureScoutingStepContent';
import { District } from '../../app/feathers/districts/District';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import {
  getDisplayedStatus,
  getDisplayedStepName,
  getProgressStepIndex,
  isStepAvailable,
  MEASURE_LIST_STEPS,
  MeasureListStepName
} from './workflow';

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const Section = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`;

type Mode = 'new' | 'existing';
type RootState = {
  user: User;
  elections?: {
    byKey: Record<string, Election | undefined>;
  };
};
type CsvRow = Record<string, unknown>;

const UploadMeasureList: React.FC = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const {
    key: electionKey,
    id,
    stepName: activeStepName
  } = useParams<{ key: string; id?: string; stepName?: string }>();
  const history = useHistory();
  const user = useSelector((state: RootState) => state.user);

  const mode: Mode = id === 'new' || !id ? 'new' : 'existing';

  const [list, setList] = useState<MeasureList | null>(null);
  const displayedStepName = getDisplayedStepName(
    activeStepName,
    list?.status,
    mode === 'new'
  );
  const displayedStepIndex = MEASURE_LIST_STEPS.findIndex(
    step => step.routeName === displayedStepName
  );
  const displayedStatus = getDisplayedStatus(displayedStepName, list?.status);
  const [loading, setLoading] = useState(false);
  const electionDataPreloaded = useSelector((state: RootState) => state.elections?.byKey[electionKey]);
  const [election, setElection] = useState<Election | null>(electionDataPreloaded || null);
  const [columnsDraft, setColumnsDraft] = useState<MeasureLabeledColumn[] | null>(null);
  const [currentTime, setCurrentTime] = useState<number>(Date.now());
  const [districtData, setDistrictData] = useState<District[]>([]);
  const [stateConfig, setStateConfig] = useState<StateConfiguration | null>(null);
  const [updating, setUpdating] = useState(false);
  const [isCancellingCreate, setIsCancellingCreate] = useState(false);
  const [cancelCreateError, setCancelCreateError] = useState<string | null>(null);
  const [regeneratingDataOperations, setRegeneratingDataOperations] = useState(false);
  const [regenerateDataOperationsError, setRegenerateDataOperationsError] = useState<string | null>(null);
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);

  // Load state configuration on initial page load (once election is known)
  useEffect(() => {
    const loadStateConfig = async () => {
      if (!feathers || !election?.stateCode) return;
      try {
        const code = election.stateCode.toLowerCase();
        const config = await feathers.getService('state-configurations').get(code);
        setStateConfig(config as StateConfiguration);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load state configuration', e);
        setStateConfig(null);
      }
    };
    loadStateConfig();
  }, [feathers, election?.stateCode]);

  // Limit district types to those that support measures (plus state)
  const measureSupportedTypes: string[] = useMemo(() => {
    const supported = (stateConfig?.typeConfigurations || [])
      .filter(tc => tc.supportsMeasures)
      .map(tc => tc.type);
    return supported.includes('state') ? supported : [...supported, 'state'];
  }, [stateConfig]);

  const columnOptions: { value: MeasureColumnLabel; label: string }[] = useMemo(() => ([
    { value: 'title', label: 'Title' },
    { value: 'ballotText', label: 'Ballot Text' },
    { value: 'actNumbers', label: 'Act Number(s)' },
    { value: 'district', label: 'District' },
  ]), []);

  // Labeling progress: rows that now have a district or a suggested creation
  const labeledRows: (MeasureLabeledDataItem & { labeledDataItemIndex: number })[] = (list && Array.isArray(list.labeledData) && list.labeledData.length > 0)
      ? list.labeledData.map((item, index) => ({ ...item, labeledDataItemIndex: index }))
      : [];

  const labelingProgress = useMemo(() => {
    const total = labeledRows.length || 0;
    if (total === 0) return { completed: 0, total: 0, percentage: 0 };
    const completed = labeledRows.filter(r => Boolean(r?.district || r?.suggestsDistrictCreationAs)).length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  }, [labeledRows]);

  const creationProgress = useMemo(() => {
    const total = list?.dataOperations?.length || 0;
    if (total === 0) return { completed: 0, total: 0, percentage: 0 };
    const completed = list?.dataOperations?.filter(op => op.status === 'completed' || op.status === 'no-op').length || 0;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  }, [list?.dataOperations]);

  const hasProcessedDataOperations = useMemo(
    () => list?.dataOperations?.some(
      operation => ['completed', 'failed', 'corrected'].includes(operation.status)
    ) ?? false,
    [list?.dataOperations]
  );
  const isCreatingDataOperations = list?.processInProgress?.stage === 'creating';
  const hasFinishedDataOperations = list?.dataOperations?.some(
    operation => ['completed', 'failed'].includes(operation.status)
  ) ?? false;
  const hasRetryableDataOperations = list?.dataOperations?.some(
    operation => ['failed', 'not-started'].includes(operation.status)
  ) ?? false;
  const canRetryDataOperations =
    !isCreatingDataOperations &&
    hasProcessedDataOperations &&
    hasRetryableDataOperations;
  const shouldDefaultToOperationsTab =
    isCreatingDataOperations || hasFinishedDataOperations;
  const dataOperationsErrorAlert = list?.dataOperationsError ? (
    <Alert severity="error" sx={{ marginBottom: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Data operation processing failed
      </Typography>
      {list.dataOperationsError}
    </Alert>
  ) : null;

  const uniqueDistrictIds = useMemo(() => {
    if (!labeledRows || labeledRows.length === 0) return [] as string[];
    const ids = labeledRows
      .map(r => r.district)
      .filter((id): id is string => Boolean(id));
    return Array.from(new Set(ids));
  }, [labeledRows]);

  const fetchDistrictData = async (districtIds: string[]) => {
    if (districtIds.length === 0) {
      setDistrictData([]);
      return;
    }
    const uniqueDistrictIds = [...new Set(districtIds)];
    try {
      const chunkSize = 20;
      const chunks: string[][] = [];
      for (let i = 0; i < uniqueDistrictIds.length; i += chunkSize) {
        chunks.push(uniqueDistrictIds.slice(i, i + chunkSize));
      }
      const responses = await Promise.all(chunks.map((chunk) => (
        feathers.getService('districts').find({ query: { _id: chunk }, $limit: chunkSize })
      )));
      const flattened = responses.flatMap((res) => res.data);
      setDistrictData(flattened || []);
    } catch (error) {
      console.error('Error fetching district data:', error);
      setDistrictData([]);
    }
  };

  useEffect(() => {
    if (uniqueDistrictIds.length > 0) {
      fetchDistrictData(uniqueDistrictIds);
    } else {
      setDistrictData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueDistrictIds.join('|')]);

  const districtById = useMemo(() => {
    const map = new Map<string, District>();
    for (const d of districtData || []) {
      if (d && d._id) map.set(d._id, d);
    }
    return map;
  }, [districtData]);

  // Detect duplicate rows based on district ID and title combination
  const duplicateAnalysis = useMemo(() => {
    if (!labeledRows || labeledRows.length === 0) return { duplicates: [], duplicateIndices: new Set<number>() };
    
    const duplicates: Array<{ groups: (MeasureLabeledDataItem & { labeledDataItemIndex: number })[], reason: string }> = [];
    const duplicateIndices = new Set<number>();
    
    // Group by district + title combination (only if district is present)
    const groupsByKey = new Map<string, { item: (MeasureLabeledDataItem & { labeledDataItemIndex: number }), index: number }[]>();
    
    labeledRows.forEach((item, index) => {
      // Only consider rows that have a district
      if (item.district && item.title) {
        const key = `${item.district}|${item.title}`;
        if (!groupsByKey.has(key)) {
          groupsByKey.set(key, []);
        }
        groupsByKey.get(key)!.push({ item, index });
      }
    });
    
    // Check for duplicates
    groupsByKey.forEach((groups, key) => {
      if (groups.length > 1) {
        duplicates.push({
          groups: groups.map(g => g.item),
          reason: 'duplicate-district-title'
        });
        
        // Add all indices to duplicate set
        groups.forEach(g => duplicateIndices.add(g.item.labeledDataItemIndex));
      }
    });
    
    return { duplicates, duplicateIndices };
  }, [labeledRows]);

  // Filter labeled rows based on duplicate filter
  const labeledRowIndecesToShow: number[] = useMemo(() => {
    if (!labeledRows || labeledRows.length === 0) return [];
    
    if (showOnlyDuplicates) {
      return Array.from(duplicateAnalysis.duplicateIndices);
    }
    
    return labeledRows.map((row) => row.labeledDataItemIndex);
  }, [showOnlyDuplicates]);

  const filteredLabeledRows = useMemo(() => {
    if (!labeledRows || labeledRows.length === 0) return [];
    if(!showOnlyDuplicates) return labeledRows;
    return labeledRows.filter((row) => labeledRowIndecesToShow.includes(row.labeledDataItemIndex));
  }, [labeledRows, labeledRowIndecesToShow, showOnlyDuplicates]);


  const loadExisting = async (recordId: string) => {
    setLoading(true);
    try {
      const response = await feathers.getService('measure-lists').get(recordId);
      setList(response);
      if(response.labeledColumns?.length > 0) {
        setColumnsDraft(response.labeledColumns);
      } else if(response.rawCsvData?.length > 0) {
        const sanitizedHeaders: string[] = Object.keys(response.rawCsvData[0] || {}).filter(h => h.trim().length > 0);
        setColumnsDraft(sanitizedHeaders.map(h => ({ columnName: h })) as MeasureLabeledColumn[]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if(!feathers) return;
    const init = async () => {
      if (!electionDataPreloaded) {
        try {
          const e = await feathers.getService('elections').get(electionKey);
          setElection(e as Election);
        } catch (e) {}
      }
      if (mode === 'existing' && id) {
        await loadExisting(id);
      }
    };
    init();
  }, [mode, id, electionKey, feathers]);

  useEffect(() => {
    if (!id || id === 'new' || !list) return;
    if (activeStepName !== displayedStepName) {
      history.replace(
        `/elections/${electionKey}/measure-lists/${id}/${displayedStepName}`
      );
    }
  }, [
    activeStepName,
    displayedStepName,
    electionKey,
    history,
    id,
    list
  ]);

  // Update current time every second when processing is in progress
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (list?.processInProgress?.stage === 'labeling' || list?.processInProgress?.stage === 'creating') {
      interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [list?.processInProgress?.stage]);

  // Poll for measure list updates every 5 seconds when processing is in progress
  useEffect(() => {
    let pollInterval: ReturnType<typeof setInterval>;
    if (list?._id && list?.processInProgress?.stage && list.processInProgress.stage !== 'none') {
      pollInterval = setInterval(async () => {
        try {
          const updated = await feathers.getService('measure-lists').get(list._id);
          setList(updated as MeasureList);
        } catch (e) {
          // no-op
        }
      }, 5000);
    }
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [list?._id, list?.processInProgress?.stage, feathers]);

  const handleCreateFromCSV = async (rows: CsvRow[], headers: string[], source: string) => {
    setLoading(true);
    try {
      const sanitizedHeaders = (headers || []).filter(h => typeof h === 'string' && h.trim().length > 0);
      const measureListData: CreateMeasureList = {
        election: electionKey,
        state: election?.state ?? '',
        createdByUser: user._id,
        rawCsvData: rows,
        status: 'started',
        source,
        labeledColumns: sanitizedHeaders.map(columnName => ({ columnName })),
        labeledData: [],
        scoutingConfiguration: [],
        markedDistricts: []
      };
      const created = await feathers.getService('measure-lists').create(measureListData);
      history.replace(`/elections/${electionKey}/measure-lists/${created._id}`);
      setList(created);
      setColumnsDraft(sanitizedHeaders.map(columnName => ({ columnName })));
    } finally {
      setLoading(false);
    }
  };

  const updateLabeledColumns = async () => {
    if (!list || !columnsDraft) return;
    setUpdating(true);
    try {
      const updated = await feathers.getService('measure-lists').patch(list._id, {
        labeledColumns: columnsDraft,
        status: 'labeled'
      });
      setList(updated as MeasureList);
    } finally {
      setUpdating(false);
    }
  };

  const confirmAndCreate = async () => {
    if (!list || isCreatingDataOperations) return;
    setUpdating(true);
    try {
      const updated = await feathers.getService('measure-lists').patch(list._id, {
        $createEntities: true
      });
      setList(updated);
    } finally {
      setUpdating(false);
    }
  };

  const cancelCreateEntities = async () => {
    if (!list || !isCreatingDataOperations || isCancellingCreate) return;

    setIsCancellingCreate(true);
    setCancelCreateError(null);
    try {
      const updated = await feathers.getService('measure-lists').patch(list._id, {
        $cancelCreateEntities: true
      });
      setList(updated);
    } catch (error) {
      console.error('Error cancelling measure entity creation:', error);
      setCancelCreateError(
        error instanceof Error
          ? error.message
          : 'Failed to cancel measure creation. Please try again.'
      );
    } finally {
      setIsCancellingCreate(false);
    }
  };

  const regenerateDataOperations = async () => {
    if (!list || regeneratingDataOperations || hasProcessedDataOperations) return;

    setRegeneratingDataOperations(true);
    setRegenerateDataOperationsError(null);
    try {
      const updated = await feathers.getService('measure-lists').patch(list._id, {
        $regenerateDataOperations: true
      });
      setList(updated);
    } catch (error) {
      console.error('Error regenerating measure data operations:', error);
      setRegenerateDataOperationsError('Unable to regenerate data operations.');
    } finally {
      setRegeneratingDataOperations(false);
    }
  };

  const saveDataOperations = async (operations: MeasureList['dataOperations']) => {
    if (!list) return;

    const previousOperations = list.dataOperations || [];
    setList(current => current ? { ...current, dataOperations: operations } : current);

    try {
      const updated = await feathers.getService('measure-lists').patch(list._id, {
        dataOperations: operations,
        dataOperationsError: null
      });
      setList(updated);
    } catch (error) {
      setList(current => current ? { ...current, dataOperations: previousOperations } : current);
      console.error('Error saving measure data operations:', error);
    }
  };

  const canProceedFromLabeled = useMemo(() => {
    if (!labeledRows || labeledRows.length === 0) return false;
    return labeledRows.every(r => Boolean(r?.district));
  }, [labeledRows]);

  const beginMatching = async () => {
    if (!list) return;

    if (list.status !== 'labeled') {
      navigateToStep('implementation-districts');
      return;
    }

    setUpdating(true);
    try {
      const updated = await feathers.getService('measure-lists').patch(list._id, {
        status: 'matched'
      });
      setList(updated);
      navigateToStep('implementation-districts');
    } finally {
      setUpdating(false);
    }
  };

  const confirmScouting = async () => {
    if (!list) return;

    if (list.status !== 'matched') {
      navigateToStep('create');
      return;
    }

    setUpdating(true);
    try {
      const updated = await feathers.getService('measure-lists').patch(list._id, {
        status: 'scouted',
        scoutingConfiguration: list.scoutingConfiguration || []
      });
      setList(updated);
      navigateToStep('create');
    } catch (err) {
      console.error('Error confirming scouting:', err);
    } finally {
      setUpdating(false);
    }
  };

  const getCurrentStep = () => {
    return displayedStepIndex;
  };

  const navigateToStep = (stepName: MeasureListStepName) => {
    if (!id || id === 'new') return;
    history.push(`/elections/${electionKey}/measure-lists/${id}/${stepName}`);
  };

  const getSteps = () => {
    const progressStepIndex = getProgressStepIndex(list?.status, mode === 'new');

    return MEASURE_LIST_STEPS.map((step, index) => {
      const available = isStepAvailable(step.routeName, list?.status, mode === 'new');
      const reviewStepIsNavigable = index >= 2 && Boolean(list?._id);

      return {
        name: step.name,
        isComplete: index < progressStepIndex || (
          index === MEASURE_LIST_STEPS.length - 1 &&
          list?.status === 'created'
        ),
        disabled: !available,
        onClick: reviewStepIsNavigable && available
          ? () => navigateToStep(step.routeName)
          : undefined
      };
    });
  };

  if (loading) {
    return (
      <Wrapper>
        <LoadingSpinner width={64} />
      </Wrapper>
    );
  }

  const Header = () => {
    return (
      <DataDetailToolbar
        onBack={{ to: { pathname: `/elections/${electionKey}/measure-lists` } }}
        navTree={[
          { text: 'Elections', to: { pathname: `/elections` } },
          { text: election?.name || '--', to: { pathname: `/elections/${electionKey}` } },
          { text: 'Measure Lists', to: { pathname: `/elections/${electionKey}/measure-lists` } },
          { text: mode === 'new' ? 'New Upload' : 'Edit Upload' }
        ]}
        navigationTrailingComponent={(
          <StepTracker
            steps={getSteps()}
            currentStep={getCurrentStep()}
            compact
            style={{ width: '560px', maxWidth: '55vw', paddingRight: '40px' }}
          />
        )}
        MoreButton={
          displayedStatus === 'scouted' && !hasProcessedDataOperations
            ? [{
              text: 'Regenerate Data Operations',
              icon: regeneratingDataOperations
                ? <CircularProgress size={16} />
                : <RefreshIcon fontSize="small" />,
              onClick: regenerateDataOperations,
              disabled: regeneratingDataOperations ||
                list?.processInProgress?.stage === 'creating'
            }]
            : undefined
        }
      />
    );
  };

  if(mode === 'new') {
    return (
      <Wrapper>
        <Header />
        <Section>
          <Typography variant="h6" style={{ marginBottom: 12 }}>Upload CSV</Typography>
          <CSVDropzone
            onProcess={({ data, headers }: { data: CsvRow[]; headers: string[] }) => handleCreateFromCSV(data, headers, 'CSV Upload')}
          />
        </Section>
      </Wrapper>
    );
  }

  if(list && displayedStatus === 'started') {
    const cols = (columnsDraft || list.labeledColumns || []);
    const required: MeasureColumnLabel[] = ['title','ballotText','district'];
    const selectedLabels = cols.filter(c => !c.discardColumn && c.columnLabel && c.columnLabel !== 'none').map(c => c.columnLabel as MeasureColumnLabel);
    const duplicateLabels = selectedLabels.filter((l, i) => selectedLabels.indexOf(l) !== i);
    const missingRequired = required.filter(r => !selectedLabels.includes(r));
    const allHeadersMappedOrDiscarded = cols.every(c => Boolean(c.discardColumn) || Boolean(c.columnLabel && c.columnLabel !== 'none'));
    const canContinue = duplicateLabels.length === 0 && missingRequired.length === 0 && allHeadersMappedOrDiscarded;

    return (
      <Wrapper>
        <Header />
        <Section>
          <Typography variant="h6" style={{ marginBottom: 12 }}>Label Columns</Typography>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <Typography variant="body2" style={{ fontWeight: 600 }}>Required:</Typography>
              {required.map(r => (
                <span key={r} style={{
                  padding: '2px 8px', borderRadius: 12, fontSize: 12,
                  background: selectedLabels.includes(r) ? '#E8F5E8' : '#FFF3E0',
                  color: selectedLabels.includes(r) ? '#2E7D32' : '#E65100',
                  border: `1px solid ${selectedLabels.includes(r) ? '#C8E6C9' : '#FFE0B2'}`
                }}>{columnOptions.find(o => o.value === r)?.label}</span>
              ))}
            </div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {cols.filter(c => (c.columnLabel && c.columnLabel !== 'none') || c.discardColumn).length}/{cols.length} mapped
            </div>
          </div>

          {/* Removed title uniqueness requirement */}

          <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', gap: 0, background: '#F7F9FE', padding: '8px 12px', borderBottom: '1px solid #eee' }}>
              <Typography variant="body2" style={{ fontWeight: 700 }}>CSV Header</Typography>
              <Typography variant="body2" style={{ fontWeight: 700 }}>Map to</Typography>
              <Typography variant="body2" style={{ fontWeight: 700 }}>Discard</Typography>
              <Typography variant="body2" style={{ fontWeight: 700 }}>Status</Typography>
            </div>
            {cols.map((col, idx) => {
              const isRequired = required.includes(col.columnLabel as MeasureColumnLabel);
              const isSelected = Boolean(col.columnLabel && col.columnLabel !== 'none');
              const isDuplicate = col.columnLabel && duplicateLabels.includes(col.columnLabel as MeasureColumnLabel);
              const statusText = col.discardColumn ? 'Discarded' : (isDuplicate ? 'Duplicate label' : (isSelected ? 'Mapped' : 'Unmapped'));
              const statusColor = col.discardColumn ? '#666' : (isDuplicate ? '#d32f2f' : (isSelected ? '#2E7D32' : '#E65100'));
              return (
                <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 1fr', gap: 0, padding: '8px 12px', borderBottom: '1px solid #eee', alignItems: 'center' }}>
                  <div style={{ fontFamily: 'monospace', fontSize: 12 }}>{col.columnName}</div>
                  <div>
                    <select
                      value={col.columnLabel || 'none'}
                      onChange={e => {
                        const current = (columnsDraft || list.labeledColumns || []).slice();
                        current[idx] = {
                          ...(current[idx] || {}),
                          columnLabel: e.target.value as MeasureColumnLabel,
                          discardColumn: false
                        };
                        setColumnsDraft(current as MeasureLabeledColumn[]);
                      }}
                      style={{ width: '100%', padding: '6px', borderRadius: 6, border: '1px solid #ddd' }}
                    >
                      <option value="none">-- None --</option>
                      {columnOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <input
                      type="checkbox"
                      checked={!!col.discardColumn}
                      onChange={e => {
                        const current = (columnsDraft || list.labeledColumns || []).slice();
                        current[idx] = {
                          ...(current[idx] || {}),
                          discardColumn: e.target.checked,
                          ...(e.target.checked ? { columnLabel: 'none' as MeasureColumnLabel } : {})
                        };
                        setColumnsDraft(current as MeasureLabeledColumn[]);
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 12, color: statusColor }}>{statusText}</div>
                </div>
              );
            })}
          </div>

          {/* Quick preview of first 3 rows */}
          {Array.isArray(list.rawCsvData) && list.rawCsvData.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Preview</Typography>
              <div style={{ border: '1px solid #eee', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', background: '#fafafa', padding: '8px 12px', gap: 8 }}>
                  <div style={{ fontWeight: 700 }}>Title</div>
                  <div style={{ fontWeight: 700 }}>Ballot Text</div>
                  <div style={{ fontWeight: 700 }}>District</div>
                </div>
                {list.rawCsvData.slice(0, 5).map((row, i) => {
                  const colsArr = (columnsDraft || list.labeledColumns || []);
                  const getVal = (label: MeasureColumnLabel | 'none') => {
                    const mapped = colsArr.find(c => c.columnLabel === label && !c.discardColumn);
                    return mapped ? row[mapped.columnName] : '';
                  };
                  return (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', padding: '8px 12px', gap: 8, borderTop: '1px solid #eee' }}>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getVal('title')}</div>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getVal('ballotText')}</div>
                      <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getVal('district')}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color={canContinue ? 'textSecondary' : 'error'}>
              {duplicateLabels.length > 0 && 'Duplicate labels selected. '}
              {missingRequired.length > 0 && `Missing required: ${missingRequired.map(r => columnOptions.find(o => o.value === r)?.label).join(', ')}`}
              {duplicateLabels.length === 0 && missingRequired.length === 0 && !allHeadersMappedOrDiscarded && 'Map or discard all headers to continue.'}
            </Typography>
            <Button variant="contained" onClick={updateLabeledColumns} disabled={!canContinue || updating}>
              {updating ? 'Processing...' : 'Next'}
            </Button>
          </div>
        </Section>
      </Wrapper>
    );
  }

  if(list && displayedStatus === 'labeled') {
    const hasActNumbers = list.labeledColumns?.some(c => c.columnLabel === 'actNumbers' && !c.discardColumn);
    
    const handleChangeDistrictByIndex = async (rowIndex: number, newDistrict: District) => {
      if(!list) return;
      const updated = (labeledRows as MeasureLabeledDataItem[]).slice();
      const index = rowIndex;
      if(index < 0 || index >= updated.length) return;
      // When DistrictCell selects, we get a District object or id; persist id when available
      const districtId = (newDistrict && (newDistrict._id || newDistrict)) || null;
      if(!districtId) return;
      updated[index] = {
        ...(updated[index] || {}),
        district: districtId as string,
        labeledBy: 'user'
      };
      try {
        const patched = await feathers.getService('measure-lists').patch(list._id, {
          labeledData: updated
        });
        setList(patched as MeasureList);
      } catch (e) {}
    };

    const handleChangeTitleByIndex = async (rowIndex: number, newTitle: string) => {
      if(!list) return;
      const updated = (labeledRows as MeasureLabeledDataItem[]).slice();
      const index = rowIndex;
      if(index < 0 || index >= updated.length) return;
      updated[index] = {
        ...(updated[index] || {}),
        title: newTitle,
        labeledBy: 'user'
      };
      try {
        const patched = await feathers.getService('measure-lists').patch(list._id, {
          labeledData: updated
        });
        setList(patched);
      } catch (e) {}
    };

    return (
      <Wrapper>
        <Header />

        {list.processInProgress?.stage === 'labeling' && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 2,
            padding: '16px',
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} sx={{ color: '#2196f3' }} />
              <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 500 }}>
                Auto-labeling {labeledRows.length} records...
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: '#1976d2' }}>
                {labelingProgress.completed} of {labelingProgress.total} labeled
              </Typography>
              <Typography variant="caption" sx={{ color: '#1976d2' }}>
                {labelingProgress.percentage}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={labelingProgress.percentage} 
              sx={{ height: 8, borderRadius: 4, backgroundColor: '#e0e0e0', '& .MuiLinearProgress-bar': { borderRadius: 4 } }}
            />
            {list.processInProgress?.startedAt && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#1976d2' }}>
                  Started: {new Date(list.processInProgress.startedAt).toLocaleTimeString()}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        <Section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Typography variant="h6">Labeled Data</Typography>
            
            {/* Duplicate Issues Summary */}
            {duplicateAnalysis.duplicates.length > 0 && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                padding: '8px 12px',
                backgroundColor: '#fff3e0',
                borderRadius: '4px',
                border: '1px solid #ffcc02'
              }}>
                <WarningIcon sx={{ color: '#f57c00' }} />
                <Typography variant="body2" sx={{ color: '#e65100', fontWeight: '500' }}>
                  {duplicateAnalysis.duplicateIndices.size} row{duplicateAnalysis.duplicateIndices.size !== 1 ? 's' : ''} have duplicate district/title combinations
                </Typography>
              </Box>
            )}
            
            {/* Filter Controls */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              alignItems: 'center',
              gap: 2
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showOnlyDuplicates}
                    onChange={(e) => setShowOnlyDuplicates(e.target.checked)}
                    color="warning"
                  />
                }
                label="Show only duplicates"
                style={{ margin: 0 }}
              />
            </Box>
          </div>
          {filteredLabeledRows.length > 0 ? (
            <div style={{ maxHeight: 600, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f5f5f5', position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #ddd', fontSize: 14, fontWeight: 'bold', width: '30%' }}>Title</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #ddd', fontSize: 14, fontWeight: 'bold', width: '40%' }}>Ballot Text</th>
                    {hasActNumbers && (
                      <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #ddd', fontSize: 14, fontWeight: 'bold', width: '15%' }}>Act Numbers</th>
                    )}  
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #ddd', fontSize: 14, fontWeight: 'bold', width: '15%' }}>Input Text</th>
                    <th style={{ padding: '12px 8px', textAlign: 'left', borderBottom: '1px solid #ddd', fontSize: 14, fontWeight: 'bold', width: '15%' }}>District</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLabeledRows.map((row: MeasureLabeledDataItem & { labeledDataItemIndex: number }, idx: number) => {
                    const isDuplicate = duplicateAnalysis.duplicateIndices.has(row.labeledDataItemIndex);
                    
                    return (
                      <tr key={row.labeledDataItemIndex} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: 14, maxWidth: 420 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isDuplicate && (
                            <WarningIcon 
                              style={{ 
                                color: '#f57c00', 
                                fontSize: '18px',
                                cursor: 'help'
                              }}
                              titleAccess="This row has a duplicate district/title combination"
                            />
                            )}
                            <TitleCell
                              title={row.title}
                              onTitleChange={(newTitle) => handleChangeTitleByIndex(row.labeledDataItemIndex, newTitle)}
                              disabled={false}
                              placeholder="Enter measure title..."
                            />
                          </div>
                        </td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: 14, maxWidth: 640 }}>
                        <Typography 
                          variant="body2" 
                          title={row.ballotText || ''}
                          style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
                        >
                          {(() => {
                            const text = row.ballotText || '';
                            return text.length > 300 ? `${text.slice(0, 300)}…` : text;
                          })()}
                        </Typography>
                      </td>
                      {
                        hasActNumbers && (
                          <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: 14, maxWidth: 240 }}>
                            <Typography variant="body2" title={row.actNumbers || ''} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {row.actNumbers || ''}
                            </Typography>
                          </td>
                        )
                      }
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: 14, maxWidth: 240 }}>{row.inputText || ''}</td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid #eee', fontSize: 14, maxWidth: 240 }}>
                        <DistrictCell
                          district={row.district ? districtById.get(row.district) : undefined}
                          suggestCreationAs={row.suggestsDistrictCreationAs}
                          onDistrictChange={(d: District) => handleChangeDistrictByIndex(row.labeledDataItemIndex, d)}
                          state={list.state}
                          loading={Boolean(row.district && !districtById.get(row.district))}
                          confidence={row.districtConfidence}
                          onChangeConfidence={undefined}
                          limitTypes={measureSupportedTypes}
                          disableEditing={false}
                        />
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ 
              padding: '24px', 
              textAlign: 'center', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '4px',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body1" color="textSecondary">
                {showOnlyDuplicates 
                  ? 'No duplicate rows found. All measures have unique district/title combinations.'
                  : 'No labeled data available.'
                }
              </Typography>
            </div>
          )}
        </Section>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={beginMatching} 
            disabled={!canProceedFromLabeled || updating}
          >
            {updating ? 'Processing...' : 'Next'}
          </Button>
        </div>
      </Wrapper>
    );
  }

  if (list && displayedStatus === 'matched') {
    return (
      <Wrapper>
        <Header />
        <Section>
          <Typography variant="h6" style={{ marginBottom: 12 }}>Implementation districts</Typography>
          <MeasureScoutingStepContent
            measureList={list}
            election={election}
            electionKey={electionKey}
            feathers={feathers}
            onConfirmScouting={confirmScouting}
            onMeasureListUpdated={setList}
            allowTypes={measureSupportedTypes}
          />
        </Section>
      </Wrapper>
    );
  }

  if (list && displayedStatus === 'scouted') {
    return (
      <Wrapper>
        <Header />
        {regenerateDataOperationsError && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {regenerateDataOperationsError}
          </Alert>
        )}
        {cancelCreateError && (
          <Alert severity="error" sx={{ marginBottom: 2 }}>
            {cancelCreateError}
          </Alert>
        )}
        {list.processInProgress?.stage === 'creating' && (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            padding: '16px',
            backgroundColor: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} sx={{ color: '#2196f3' }} />
              <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 500 }}>
                Creating entities from {list.dataOperations?.length || 0} operations...
              </Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={cancelCreateEntities}
                disabled={isCancellingCreate}
                sx={{ marginLeft: 'auto' }}
              >
                {isCancellingCreate ? 'Cancelling...' : 'Cancel'}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="caption" sx={{ color: '#1976d2' }}>
                {creationProgress.completed} of {creationProgress.total} operations completed
              </Typography>
              <Typography variant="caption" sx={{ color: '#1976d2' }}>
                {creationProgress.percentage}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={creationProgress.percentage}
              sx={{ height: 8, borderRadius: 4, backgroundColor: '#e0e0e0', '& .MuiLinearProgress-bar': { borderRadius: 4 } }}
            />
            {list.processInProgress?.startedAt && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" sx={{ color: '#1976d2' }}>
                  Started: {new Date(list.processInProgress.startedAt).toLocaleTimeString()}
                </Typography>
                <Typography variant="caption" sx={{ color: '#1976d2' }}>
                  {' • Elapsed: '}
                  {(() => {
                    const elapsedMs = currentTime - new Date(list.processInProgress.startedAt).getTime();
                    const elapsedMinutes = Math.floor(elapsedMs / 60000);
                    const elapsedSeconds = Math.floor((elapsedMs % 60000) / 1000);
                    if (elapsedMinutes >= 60) {
                      return `${Math.floor(elapsedMinutes / 60)}h ${elapsedMinutes % 60}m ${elapsedSeconds}s`;
                    }
                    return `${elapsedMinutes}m ${elapsedSeconds}s`;
                  })()}
                </Typography>
              </Box>
            )}
            {list.processInProgress?.startedAt &&
              currentTime - new Date(list.processInProgress.startedAt).getTime() > 3600000 && (
                <Alert severity="warning">
                  This shouldn't be taking this long. Contact an admin.
                </Alert>
            )}
          </Box>
        )}

        {dataOperationsErrorAlert}
        {Array.isArray(list.dataOperations) && list.dataOperations.length > 0 && (
          <Section>
            <DataOperationsTable
              dataOperations={list.dataOperations}
              title="Data Operations Queue"
              electionKey={electionKey}
              onChangeDataOperations={saveDataOperations}
              editingDisabled={isCreatingDataOperations}
              defaultToOperationsTab={shouldDefaultToOperationsTab}
              onRetryFailedOperations={
                canRetryDataOperations ? confirmAndCreate : undefined
              }
            />
            {
              !canRetryDataOperations && list.processInProgress?.stage !== 'creating' &&
              <div style={{ marginTop: 12 }}>
                <Button variant="contained" color="primary" onClick={confirmAndCreate} disabled={updating}>
                  {updating ? 'Processing...' : 'Confirm and Create'}
                </Button>
              </div>
            }
          </Section>
        )}
      </Wrapper>
    );
  }

  if(list && displayedStatus === 'created') {
    return (
      <Wrapper>
        <Header />
        <Section>
          {cancelCreateError && (
            <Alert severity="error" sx={{ marginBottom: 2 }}>
              {cancelCreateError}
            </Alert>
          )}
          {list.processInProgress?.stage === 'creating' && (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              padding: '16px',
              backgroundColor: '#e3f2fd',
              border: '1px solid #2196f3',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={24} sx={{ color: '#2196f3' }} />
                <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 500 }}>
                  Retrying incomplete operations...
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={cancelCreateEntities}
                  disabled={isCancellingCreate}
                  sx={{ marginLeft: 'auto' }}
                >
                  {isCancellingCreate ? 'Cancelling...' : 'Cancel'}
                </Button>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Progress: {creationProgress.completed} of {creationProgress.total} operations completed
              </Typography>
              <LinearProgress
                variant="determinate"
                value={creationProgress.percentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': { borderRadius: 4 }
                }}
              />
            </Box>
          )}

          {/* Error Title for Failed Operations */}
          {list.dataOperations && list.dataOperations.some(op => op.status === 'failed') && (
            <Typography variant="h2" style={{ marginBottom: '16px', color: '#d32f2f' }}>
              ⚠️ Creation Completed with Errors
            </Typography>
          )}
          
          {/* Success Title for All Operations Successful */}
          {list.dataOperations && !list.dataOperations.some(op => op.status === 'failed') && (
            <Typography variant="h2" style={{ marginBottom: '16px', color: '#2e7d32' }}>
              ✓ Creation Complete!
            </Typography>
          )}
          
          <Typography variant="body1" style={{ marginBottom: '24px', color: '#666' }}>
            Your measure list has been successfully created. You will be redirected to the election page shortly.
          </Typography>
          
          <div style={{ 
            padding: '24px', 
            textAlign: 'center', 
            backgroundColor: list.dataOperations && list.dataOperations.some(op => op.status === 'failed') ? '#ffebee' : '#e8f5e8', 
            borderRadius: '4px',
            border: `1px solid ${list.dataOperations && list.dataOperations.some(op => op.status === 'failed') ? '#f44336' : '#4caf50'}`
          }}>
            <Typography variant="body1" style={{ 
              fontWeight: 'bold',
              color: list.dataOperations && list.dataOperations.some(op => op.status === 'failed') ? '#d32f2f' : '#2e7d32'
            }}>
              {list.dataOperations && list.dataOperations.some(op => op.status === 'failed') 
                ? '⚠️ Measure list creation completed with some errors' 
                : '✓ Measure list creation completed successfully'
              }
            </Typography>
          </div>
          
          {/* Final Data Operations Summary */}
          {dataOperationsErrorAlert}
          {Array.isArray(list.dataOperations) && list.dataOperations.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <DataOperationsTable
                dataOperations={list.dataOperations}
                title="Final Operation Results"
                electionKey={electionKey}
                onChangeDataOperations={saveDataOperations}
                editingDisabled={isCreatingDataOperations}
                defaultToOperationsTab={shouldDefaultToOperationsTab}
                onRetryFailedOperations={
                  canRetryDataOperations
                    ? confirmAndCreate
                    : undefined
                }
              />
            </div>
          )}
        </Section>
      </Wrapper>
    );
  }

  return null;
};

export default UploadMeasureList;


